import { memo, ReactElement, useMemo } from "react"
import styled from "styled-components"
import { useAppSelector } from "reducers/store"
import { useQueryState } from "context/queryState"
import InfoCardItem from "./InfoCard"
import { ACCOUNT_BALANCE_DETAILS } from "reducers/accounts/actions-types"
import ApiAccounts from "api/ApiAccounts"
import Heading from "components/Typography/Heading"
import BalanceCard from "./BalanceCard"
import IconPlans from "../icons/IconPlans"
import IconAccumulatedBalance from "../icons/IconAccumulatedBalance"
import IconOutstandingBalance from "../icons/IconOutstandingBalance"
import Spinner from "components/EvtKit/spinner/Spinner"

const Cards = styled.div`
  width: 100%;
  display: flex;
  gap: 32px;
  flex-direction: column;
`

const InfoCardsContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 24px;
  width: 100%;

  & > :first-child:nth-last-child(odd) {
    grid-column: 1 / -1;
  }
`

const BalanceContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
`

const BalanceCardsContainer = styled.div`
  display: flex;
  flex-direction: row;
  gap: 24px;
  width: 100%;
`

const BoxContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  width: 100%;
  padding: 16px;
  border-radius: 8px;
  background-color: ${(props) => props.theme.palette.white};
  box-shadow: 0px 2px 6px 0px rgba(13, 10, 44, 0.08);
  height: 60px;
`

const AccountDetailsCards = (): ReactElement => {
  const { account } = useAppSelector((state) => state.accounts)

  const { accountDetails } = useAppSelector((state) => state.accounts)

  const { data, loading } = accountDetails.balance

  const { currentBillingValue, globalBillingValue, selectedPlan, eventTypesUsageInfo, isFreeTrialActive } = data

  const searchParams = useMemo(() => {
    return {
      accountId: account?.id
    }
  }, [account.id])

  const freeTrialInfo = useMemo(() => {
    const showFreeTrial = isFreeTrialActive || !selectedPlan || selectedPlan.isCanceled

    if (isFreeTrialActive)
      return {
        status: "Ativo",
        showFreeTrial
      }

    return {
      status: "Expirado",
      showFreeTrial
    }
  }, [selectedPlan, isFreeTrialActive])

  const showCurrentBillingValue = useMemo(() => {
    return !selectedPlan?.hasRecurrence && !selectedPlan?.isEnterprise && !freeTrialInfo.showFreeTrial
  }, [selectedPlan, freeTrialInfo.showFreeTrial])

  useQueryState(
    [ACCOUNT_BALANCE_DETAILS, { ...searchParams }],
    async () =>
      new ApiAccounts().getAccountBalanceDetails({
        ...searchParams
      }),
    ACCOUNT_BALANCE_DETAILS
  )

  return (
    <>
      {!loading && (
        <Cards>
          <InfoCardsContainer>
            {freeTrialInfo.showFreeTrial && (
              <InfoCardItem
                icon={<IconPlans />}
                value="Free Trial"
                description={freeTrialInfo.status}
                tooltipText="Status do Free Trial"
                descriptionColor={freeTrialInfo.status === "Expirado" ? "error" : "gray-dark"}
              />
            )}
            {!!selectedPlan && (
              <InfoCardItem
                icon={<IconPlans />}
                value={selectedPlan.name}
                description={selectedPlan.isCanceled ? "Plano Cancelado" : "Plano Atual"}
                tooltipText={
                  selectedPlan.isCanceled ? "Nome do plano cancelado da conta" : "Nome do plano atual da conta"
                }
                descriptionColor={selectedPlan.isCanceled ? "error" : "gray-dark"}
              />
            )}
            {showCurrentBillingValue && (
              <InfoCardItem
                icon={<IconAccumulatedBalance />}
                value={currentBillingValue}
                description="Acumulados para cobrança"
                tooltipText="Valor total acumulado para cobrança"
                isPrice
              />
            )}
            <InfoCardItem
              icon={<IconOutstandingBalance />}
              value={globalBillingValue}
              description="Saldo devedor total"
              tooltipText="Valor total pendente para cobrança"
              isPrice
            />
          </InfoCardsContainer>
          {eventTypesUsageInfo.length > 0 && (
            <BalanceContainer>
              <Heading variant="h5" color="primary-dark">
                Balanço
              </Heading>
              <BalanceCardsContainer>
                {eventTypesUsageInfo.map((eventTypeUsageInfo) => (
                  <BalanceCard key={eventTypeUsageInfo.label} eventType={eventTypeUsageInfo} />
                ))}
              </BalanceCardsContainer>
            </BalanceContainer>
          )}
        </Cards>
      )}
      {loading && (
        <BoxContainer>
          <Spinner className="secondary" />
        </BoxContainer>
      )}
    </>
  )
}

export default memo(AccountDetailsCards)
